import React, {
  useState, useCallback, useRef, useMemo,
} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import classnames from 'classnames';
import _ from 'lodash';
import Carousel from 'antd/lib/carousel';

import getAxiosInstance, {BASE_URL_TYPE} from '../../api/axios';
import PopupBase from './PopupBase';
import * as actionCreators from '../../store/actions';

const axios = getAxiosInstance();
const propTypes = {};

const siteUrl = `${BASE_URL_TYPE.PROPZONE}/themes/custom/gridics/images/onboarding/`;
const steps = [
  {
    image: `${siteUrl}search-data.svg?v1`,
    title: 'Search Robust Property Data',
    content: 'A Site Selection tool that goes beyond basic zoning limits.',
  },
  {
    image: `${siteUrl}analyze-development.svg`,
    title: 'Analyze Zoned Development',
    content:
      'Review preliminary development potential of any parcel without the need to understand complex zoning code.',
  },
  {
    image: `${siteUrl}save-properties.svg`,
    title: 'Save Properties, Notes, and Attachments',
    content: 'Save information to quickly find the material you need from any device.',
  },
];

function OnBoardingPopup() {
  // States.
  const [currentStep, setCurrentStep] = useState(0);

  // Refs.
  const popupRef = useRef();
  const carouselRef = useRef();

  // Get redux states.
  const userData = useSelector(state => state.user.userData);
  const isMobile = useSelector(state => state.ui.isMobile);

  // Set dispatchers.
  const dispatch = useDispatch();
  const updateUserData = useCallback(data => dispatch(actionCreators.updateUserData(data)), [
    dispatch,
  ]);

  const goToSlide = (number) => {
    carouselRef.current.goTo(number);
    setCurrentStep(number);
  };

  const onClose = () => {
    // Save information about accepted onboard info.
    axios
      .post(
        `${BASE_URL_TYPE.PROPZONE}/api/v1/sso-user`,
        {
          data: {onboard_disabled: true},
        },
        {
          withCredentials: true,
          headers: {
            'X-CSRF-Token': userData.token,
          },
        },
      )
      .then((data) => {
        updateUserData(data.data);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const progress = useMemo(() => {
    const items = [];
    for (let i = 0; i < steps.length; i += 1) {
      items.push(
        <button
          key={i}
          type="button"
          className={classnames('no-style indicator', {active: currentStep === i})}
          onClick={() => {
            setCurrentStep(i);
            goToSlide(i);
          }}
        />,
      );
    }
    return items;
  }, [currentStep]);

  const slider = useMemo(() => {
    const items = [];
    _.each(steps, (step, stepKey) => {
      items.push(
        <div className="step" key={stepKey}>
          <div className="img">
            <img alt={step.title} src={step.image} />
          </div>
          <h4>{step.title}</h4>
          <p>{step.content}</p>
        </div>,
      );
    });
    const settings = {
      dots: false,
      infinite: false,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      swipeToSlide: true,
      draggable: true,
      afterChange: (current) => {
        setCurrentStep(current);
      },
    };
    return (
      <Carousel ref={carouselRef} {...settings}>
        {items}
      </Carousel>
    );
  }, []);

  return (
    <PopupBase className="on-board" closeEvent={onClose} ref={popupRef} closeButton={false}>
      <React.Fragment>
        {slider}
        <div className="bottom-content">
          <button
            type="button"
            className={classnames('get-started btn-secondary', {
              hidden: currentStep < steps.length - 1,
            })}
            onClick={(e) => {
              e.preventDefault();
              popupRef.current.close();
            }}
          >
            Get Started
          </button>
          <div className="progress">{progress}</div>
        </div>
        {!isMobile && (
          <div className="nav">
            {steps[currentStep - 1] && (
              <button
                type="button"
                className="no-style prev"
                onClick={(e) => {
                  e.preventDefault();
                  goToSlide(currentStep - 1);
                }}
              >
                <i className="icon-b icon-b-arrow-left" />
              </button>
            )}
            {steps[currentStep + 1] && (
              <button
                type="button"
                className="no-style next"
                onClick={(e) => {
                  e.preventDefault();
                  goToSlide(currentStep + 1);
                }}
              >
                <i className="icon-b icon-b-arrow-right" />
              </button>
            )}
          </div>
        )}
      </React.Fragment>
    </PopupBase>
  );
}

OnBoardingPopup.propTypes = propTypes;

export default OnBoardingPopup;
